import { useFormContext, useController } from 'react-hook-form';
import { ErrorLabel, Label, SuccessLabel } from '@/atoms/forms/labels';
import Spinner from '@/atoms/loader/Spinner';
import FieldLayout from '../../FieldLayout';
import { INPUT_STATE, InputProps } from '../types';
import { computeState } from '../utils';
import HTMLInput from './HTMLInput';
import { ChangeEventHandler } from 'react';
import clx from 'classnames';

const TextInput = (props: InputProps) => {
  const { label, name, className, validMessage, disabled, hint, required, onChange, transform, icon, ...restProps } =
    props;
  const { control, formState } = useFormContext();
  const { field } = useController({
    name,
    control,
  });
  const { errors, isValidating, dirtyFields } = formState;
  const error = errors[name];
  const state = computeState(props, formState);

  const handleChange: ChangeEventHandler<HTMLInputElement> | undefined = (event) => {
    const newEvent = transform?.output ? transform.output(event) : event;
    field.onChange(newEvent);

    if (onChange) {
      onChange(newEvent);
    }
  };

  return (
    <FieldLayout className={className}>
      {label && <Label disabled={disabled} label={label} name={name} required={required} />}
      <div className="relative">
        {validMessage && !!dirtyFields[name] && !!isValidating && <Spinner className="absolute right-3 top-2" />}
        {icon && (
          <i
            className={clx(
              'text-neutral-500 hover:bg-neutral-50 active:bg-mercury-200 absolute left-2 top-1/2 -translate-y-1/2 fa-solid',
              icon && icon,
            )}
          />
        )}
        <HTMLInput
          {...restProps}
          disabled={disabled}
          {...field}
          className={icon ? '!pl-9' : ''}
          id={name}
          onChange={handleChange}
          state={state}
          value={field.value || ''}
        />
        {hint && <p className="sm-regular text-neutral-600">{hint}</p>}
      </div>
      {!!error && <ErrorLabel error={error.message} />}
      {validMessage && state === INPUT_STATE.SUCCESS && <SuccessLabel text={validMessage} />}
    </FieldLayout>
  );
};

export default TextInput;
